/**
 * VehicleHistoryTab — Aba "Histórico" do modal de detalhes do veículo
 *
 * Réplica da estrutura do TaskHistoryTab, adaptada aos eventos do veículo
 * (cadastro, edição, consulta por placa, sincronização de multas e
 * vínculos com negócios, contratos, tarefas, clientes e contatos).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-02 v1.1.0 — Eventos de auditoria: status, vistorias, financeiro e tarefas
 * RELEASE: 2026-08-09 v1.0.0 — Criação inicial (paridade com o modal de tarefas)
 */
import { useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Link2,
  Unlink,
  Search,
  ReceiptText,
  History,
  Activity,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  useVehicleActivityLog,
  type VehicleActivityLogEntry,
} from '@/hooks/useVehicleActivityLog';
import { VEHICLE_AUDIT_LABELS, describeVehicleAuditEvent } from './vehicleAuditEvents';

interface VehicleHistoryTabProps {
  vehicleId: string;
  /** Só consulta quando a aba está visível */
  isActive?: boolean;
}

/** Mapeamento ação → ícone, rótulo e cor. */
const ACTION_META: Record<string, { icon: typeof Plus; label: string; tone: string }> = {
  created:            { icon: Plus,        label: 'Veículo cadastrado',            tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  updated:            { icon: Pencil,      label: 'Dados do veículo atualizados',  tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  deleted:            { icon: Trash2,      label: 'Veículo excluído',              tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  plate_lookup:       { icon: Search,      label: 'Consulta por placa realizada',  tone: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  fines_sync:         { icon: ReceiptText, label: 'Multas sincronizadas',          tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  deal_linked:        { icon: Link2,       label: 'Vinculado a um negócio',        tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  deal_unlinked:      { icon: Unlink,      label: 'Desvinculado de um negócio',    tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  contract_linked:    { icon: Link2,       label: 'Vinculado a um contrato',       tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  contract_unlinked:  { icon: Unlink,      label: 'Desvinculado de um contrato',   tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  task_linked:        { icon: Link2,       label: 'Vinculado a uma tarefa',        tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  task_unlinked:      { icon: Unlink,      label: 'Desvinculado de uma tarefa',    tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  customer_linked:    { icon: Link2,       label: 'Vinculado a um cliente',        tone: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  customer_unlinked:  { icon: Unlink,      label: 'Desvinculado do cliente',       tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  customer_changed:   { icon: Link2,       label: 'Cliente alterado',              tone: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  // v1.2.0 do trigger: troca de proprietário do veículo
  vehicle_transferred:{ icon: Link2,       label: 'Transferido para outro cliente', tone: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  contact_linked:     { icon: Link2,       label: 'Vinculado a um contato',        tone: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  contact_unlinked:   { icon: Unlink,      label: 'Desvinculado do contato',       tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  contact_changed:    { icon: Link2,       label: 'Contato alterado',              tone: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  note_created:       { icon: Activity,    label: 'Andamento registrado',          tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  // v1.1.0: eventos de auditoria (nomes reais gravados pelos gatilhos)
  vehicle_created:          { icon: Plus,        label: VEHICLE_AUDIT_LABELS.vehicle_created,          tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  vehicle_updated:          { icon: Pencil,      label: VEHICLE_AUDIT_LABELS.vehicle_updated,          tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  vehicle_status_changed:   { icon: Activity,    label: VEHICLE_AUDIT_LABELS.vehicle_status_changed,   tone: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  inspection_created:       { icon: Plus,        label: VEHICLE_AUDIT_LABELS.inspection_created,       tone: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  inspection_status_changed:{ icon: Activity,    label: VEHICLE_AUDIT_LABELS.inspection_status_changed,tone: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  charge_linked:            { icon: ReceiptText, label: VEHICLE_AUDIT_LABELS.charge_linked,            tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  charge_unlinked:          { icon: Unlink,      label: VEHICLE_AUDIT_LABELS.charge_unlinked,          tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  subscription_linked:      { icon: ReceiptText, label: VEHICLE_AUDIT_LABELS.subscription_linked,      tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  subscription_unlinked:    { icon: Unlink,      label: VEHICLE_AUDIT_LABELS.subscription_unlinked,    tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  task_status_changed:      { icon: Activity,    label: VEHICLE_AUDIT_LABELS.task_status_changed,      tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  task_phase_changed:       { icon: Activity,    label: VEHICLE_AUDIT_LABELS.task_phase_changed,       tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
};

/** Rótulos dos campos do veículo (usados no detalhamento das mudanças). */
const FIELD_LABEL: Record<string, string> = {
  plate: 'Placa',
  renavam: 'Renavam',
  chassis: 'Chassi',
  color: 'Cor',
  fuel: 'Combustível',
  year: 'Ano',
  year_code: 'Código do ano',
  brand_name: 'Marca',
  model_name: 'Modelo',
  vehicle_type: 'Tipo',
  category_id: 'Categoria',
  fipe_code: 'Código FIPE',
  fipe_value: 'Valor FIPE',
  notes: 'Observações',
  customer_id: 'Cliente',
  contact_id: 'Contato',
};

/** Campos que armazenam UUID e devem ser resolvidos via `labels`. */
const UUID_FIELDS = new Set(['customer_id', 'contact_id', 'category_id']);

/** Truncamento de UUID (padrão UI do projeto: 8 chars maiúsculas). */
function shortId(value: unknown): string {
  if (typeof value !== 'string') return '—';
  return value.length >= 8 ? value.slice(0, 8).toUpperCase() : value;
}

/** Renderiza valor "humanizado" para um campo conhecido. */
function renderValue(field: string, value: unknown, labels?: Record<string, string>): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'fipe_value') {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  }
  if (UUID_FIELDS.has(field) && typeof value === 'string') {
    const name = labels?.[value];
    const sid = shortId(value);
    return name ? `${name} (${sid})` : sid;
  }
  return String(value);
}

/** Nome do registro relacionado (negócio/tarefa/contrato/cliente/contato). */
function relatedLabel(entry: VehicleActivityLogEntry): string | null {
  const meta = entry.metadata || {};
  const keys = ['deal_id', 'task_id', 'contract_id', 'customer_id', 'contact_id'];
  for (const key of keys) {
    const id = meta[key];
    if (typeof id === 'string') {
      return entry.labels?.[id] || shortId(id);
    }
  }
  return null;
}

/** Detalhamento das mudanças de campos. */
function ChangeLines({ entry }: { entry: VehicleActivityLogEntry }) {
  const fields = Object.keys(entry.changes || {});
  if (fields.length === 0) return null;
  const labels = entry.labels;

  return (
    <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
      {fields.map((field) => {
        const change = entry.changes[field] as { from?: unknown; to?: unknown; changed?: boolean };
        const label = FIELD_LABEL[field] || field;

        if (change?.changed && change.from === undefined && change.to === undefined) {
          return (
            <li key={field}>
              <span className="font-medium text-foreground">{label}</span>: alterado
            </li>
          );
        }

        return (
          <li key={field} className="break-words">
            <span className="font-medium text-foreground">{label}</span>:{' '}
            <span className="line-through opacity-70">
              {renderValue(field, change?.from, labels)}
            </span>
            {' → '}
            <span className="text-foreground">{renderValue(field, change?.to, labels)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Aba de histórico (timeline vertical) */
export function VehicleHistoryTab({ vehicleId, isActive = true }: VehicleHistoryTabProps) {
  const { logs, isLoading, error } = useVehicleActivityLog(vehicleId, { enabled: isActive });

  // Mais recente no topo
  const ordered = useMemo(
    () =>
      [...logs].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [logs],
  );

  if (isLoading) {
    return (
      <div className="space-y-3 p-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Não foi possível carregar o histórico deste veículo.
      </div>
    );
  }

  if (ordered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4">
        <History className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium text-foreground">Sem histórico ainda</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-xs">
          As alterações deste veículo aparecerão aqui automaticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="relative pl-3 pr-1 py-2">
      {/* Linha vertical da timeline */}
      <div className="absolute left-[26px] top-3 bottom-3 w-px bg-border/60" />

      <ol className="space-y-3">
        {ordered.map((entry) => {
          const meta = ACTION_META[entry.action] || {
            icon: Pencil,
            label: entry.action,
            tone: 'bg-muted text-muted-foreground',
          };
          const Icon = meta.icon;
          const created = new Date(entry.created_at);
          const related = relatedLabel(entry);

          return (
            <li key={entry.id} className="relative flex gap-3">
              {/* Ícone da ação */}
              <div
                className={cn(
                  'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  meta.tone,
                )}
              >
                <Icon className="h-4 w-4" />
              </div>

              {/* Conteúdo do evento */}
              <div className="flex-1 min-w-0 rounded-lg border bg-card px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {meta.label}
                    {related && (
                      <span className="text-muted-foreground font-normal"> · {related}</span>
                    )}
                  </span>
                  <span
                    className="text-[11px] text-muted-foreground"
                    title={format(created, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  >
                    {formatDistanceToNow(created, { addSuffix: true, locale: ptBR })}
                  </span>
                </div>

                {/* Autor */}
                <div className="mt-1 flex items-center gap-1.5">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={entry.performer_avatar || undefined} />
                    <AvatarFallback className="text-[9px]">
                      {(entry.performer_name || 'S').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-muted-foreground">
                    {entry.performer_name || 'Sistema'}
                  </span>
                </div>

                {/* v1.1.0: detalhe "de → para" dos eventos de auditoria */}
                {describeVehicleAuditEvent(entry.action, entry.metadata) && (
                  <p className="mt-1.5 text-xs text-muted-foreground break-words">
                    {describeVehicleAuditEvent(entry.action, entry.metadata)}
                  </p>
                )}

                <ChangeLines entry={entry} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
